import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismNumberInput } from '@mylighthouse/prism-ember';
import type { CustomEmberTableColumn } from '@mylighthouse/prism-ember/types/components/prism/table';
import type { RecommendationStrategy } from 'lumen/models';
import type { DynamicRateDiscount } from 'lumen/models/recommendation-strategy';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import { toDisplayValue } from 'lumen/utils/pricing-assistant/value-conversion';
import { localCopy } from 'tracked-toolbox';

type DiscountSetting = 'maximum' | 'minimum';

export type DynamicRateRangesDiscountColumn = CustomEmberTableColumn & {
  isEditing: boolean;
  onDiscountChange: (
    rate: string,
    setting: DiscountSetting,
    value: number | null,
  ) => void;
  saveAttempted: boolean;
  strategy: RecommendationStrategy;
  valuePath: DiscountSetting;
};

interface SettingsRecommendationsDynamicRateRangesTdDiscountSignature {
  Args: {
    column: DynamicRateRangesDiscountColumn;
    row: { rate: string };
    value: DynamicRateDiscount | null;
  };
}

export default class SettingsRecommendationsDynamicRateRangesTdDiscount extends Component<SettingsRecommendationsDynamicRateRangesTdDiscountSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;

  @service declare intl: Services['intl'];

  /* The value currently in the input. `@localCopy` re-seeds it whenever
   * `displayValue` changes externally — notably when Cancel rolls back the
   * strategy — so the input reverts with it instead of keeping the typed value.
   */
  @localCopy('displayValue') declare liveValue: number | null;

  // The saved discount as an input value (BE stored -> FE display), or null when unset.
  get displayValue(): number | null {
    const { value } = this.args;

    return value ? toDisplayValue(value.value, value.type) : null;
  }

  get formattedValue(): string {
    const { value } = this.args;
    const { displayValue } = this;

    if (value === null || displayValue === null) {
      return this.placeholder;
    }

    return value.type === 'relative'
      ? `${this.intl.formatNumber(displayValue, { maximumFractionDigits: 2 })}%`
      : String(displayValue);
  }

  /* Absolute discounts are amounts (currency), relative discounts are percentages.
   * An unset cell has no type yet; new entries default to relative, so show '%'.
   */
  get inlineLabel(): string {
    return this.args.value?.type === 'absolute'
      ? this.hotelAndMarketService.hotel.currencySymbol
      : '%';
  }

  /* Mirror price-range's td-dow: the invalid state only surfaces once Save has
   * been attempted, driven by the model's validation of the whole rate row.
   */
  get numberInputHasErrors(): boolean {
    const { column, row } = this.args;

    if (!column.saveAttempted) {
      return false;
    }

    return column.strategy.dynamicRateDiscountRangesWithError.includes(
      row.rate,
    );
  }

  get placeholder(): string {
    return this.args.column.valuePath === 'maximum'
      ? this.intl.t('pricing_assistant.recommendations.price_range.no_max')
      : this.intl.t('pricing_assistant.recommendations.price_range.no_min');
  }

  /* Per-cell hook (rate + setting) so tests can target one input or value
   * without depending on the table's cell ordering.
   */
  get testId(): string {
    const { rate } = this.args.row;

    return `td-dynamic-rate-range-discount-${rate}-${this.args.column.valuePath}`;
  }

  /* Commit on blur. Blur fires before a Save click, so the value always reaches
   * the strategy before it's persisted.
   */
  @action onDiscountFocusOut(): void {
    const { column, row } = this.args;
    // Skip a no-op commit that would needlessly dirty the strategy.
    if (this.liveValue === this.displayValue) {
      return;
    }

    column.onDiscountChange(row.rate, column.valuePath, this.liveValue);
  }

  @action onDiscountValueChange(value: number | null): void {
    this.liveValue = value;
  }

  <template>
    {{#if @column.isEditing}}
      <PrismNumberInput
        @hasErrors={{this.numberInputHasErrors}}
        @inlineLabel={{this.inlineLabel}}
        @isSmall={{true}}
        @isTouched={{true}}
        @onFocusOut={{this.onDiscountFocusOut}}
        @onValueChange={{this.onDiscountValueChange}}
        @placeholder={{this.placeholder}}
        @removePadding={{true}}
        @value={{this.liveValue}}
        data-test-has-error={{if this.numberInputHasErrors "true" "false"}}
        data-test-id={{this.testId}}
      />
    {{else}}
      <span data-test-id={{this.testId}}>{{this.formattedValue}}</span>
    {{/if}}
  </template>
}
